import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, startOfDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface DateFilterBarProps {
  pendingFrom: Date | undefined;
  pendingTo: Date | undefined;
  hasPendingChange: boolean;
  onSelectFrom: (d: Date | undefined) => void;
  onSelectTo: (d: Date | undefined) => void;
  onApply: () => void;
  onReset: () => void;
  onPresetApply?: (from: Date, to: Date) => void;
}

interface Preset {
  label: string;
  getRange: () => { from: Date; to: Date };
}

const getPresets = (): Preset[] => {
  const today = startOfDay(new Date());
  return [
    { label: 'Vandaag', getRange: () => ({ from: today, to: today }) },
    { label: 'Gisteren', getRange: () => ({ from: subDays(today, 1), to: subDays(today, 1) }) },
    { label: 'Vandaag + gisteren', getRange: () => ({ from: subDays(today, 1), to: today }) },
    { label: 'Afgelopen 7 dagen', getRange: () => ({ from: subDays(today, 6), to: today }) },
    { label: 'Afgelopen 14 dagen', getRange: () => ({ from: subDays(today, 13), to: today }) },
    { label: 'Afgelopen 28 dagen', getRange: () => ({ from: subDays(today, 27), to: today }) },
    { label: 'Afgelopen 30 dagen', getRange: () => ({ from: subDays(today, 29), to: today }) },
    { label: 'Deze week', getRange: () => ({ from: startOfWeek(today, { locale: nl }), to: today }) },
    { label: 'Vorige week', getRange: () => {
      const lastWeekStart = startOfWeek(subWeeks(today, 1), { locale: nl });
      return { from: lastWeekStart, to: endOfWeek(lastWeekStart, { locale: nl }) };
    }},
    { label: 'Deze maand', getRange: () => ({ from: startOfMonth(today), to: today }) },
    { label: 'Vorige maand', getRange: () => {
      const lastMonth = subMonths(today, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }},
    { label: 'Maximum', getRange: () => ({ from: new Date('2026-01-01'), to: today }) },
  ];
};

const DateFilterBar = ({ pendingFrom, pendingTo, hasPendingChange, onSelectFrom, onSelectTo, onApply, onReset, onPresetApply }: DateFilterBarProps) => {
  const [showPresets, setShowPresets] = useState(false);
  const presets = getPresets();

  const activePresetLabel = (() => {
    if (!pendingFrom || !pendingTo) return null;
    const fromStr = format(pendingFrom, 'yyyy-MM-dd');
    const toStr = format(pendingTo, 'yyyy-MM-dd');
    for (const p of presets) {
      const r = p.getRange();
      if (format(r.from, 'yyyy-MM-dd') === fromStr && format(r.to, 'yyyy-MM-dd') === toStr) {
        return p.label;
      }
    }
    return null;
  })();

  const handlePreset = (preset: Preset) => {
    const { from, to } = preset.getRange();
    onSelectFrom(from);
    onSelectTo(to);
    setShowPresets(false);
    // Auto-apply if callback provided
    if (onPresetApply) {
      onPresetApply(from, to);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Preset dropdown */}
      <Popover open={showPresets} onOpenChange={setShowPresets}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="text-xs font-medium gap-1.5">
            <CalendarIcon className="h-3 w-3" />
            {activePresetLabel || 'Aangepast'}
            <span className="text-[10px] text-gray-400 ml-0.5">▼</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[180px] p-1 pointer-events-auto" align="start">
          <div className="flex flex-col">
            {presets.map(preset => {
              const isActive = activePresetLabel === preset.label;
              return (
                <button
                  key={preset.label}
                  onClick={() => handlePreset(preset)}
                  className={cn(
                    "text-left text-xs px-3 py-1.5 rounded-md transition",
                    isActive
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* From date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn("w-[140px] justify-start text-left font-normal text-xs", !pendingFrom && "text-muted-foreground")}>
            <CalendarIcon className="mr-1 h-3 w-3" />
            {pendingFrom ? format(pendingFrom, 'dd MMM yyyy', { locale: nl }) : 'Van'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={pendingFrom} onSelect={onSelectFrom} initialFocus className="p-3 pointer-events-auto" />
        </PopoverContent>
      </Popover>

      <span className="text-muted-foreground text-xs">→</span>

      {/* To date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn("w-[140px] justify-start text-left font-normal text-xs", !pendingTo && "text-muted-foreground")}>
            <CalendarIcon className="mr-1 h-3 w-3" />
            {pendingTo ? format(pendingTo, 'dd MMM yyyy', { locale: nl }) : 'Tot'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={pendingTo} onSelect={onSelectTo} initialFocus className="p-3 pointer-events-auto" />
        </PopoverContent>
      </Popover>

      {hasPendingChange && (
        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3" onClick={onApply}>
          Toepassen
        </Button>
      )}
      <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={onReset}>
        Reset
      </Button>
    </div>
  );
};

export default DateFilterBar;